import type {Ref} from 'vue'
import {filters as _hotelFilters} from '@/utils/hotels/hotels-blank-states'
import {filters as _flightFilters} from '~src/utils/flights/flights-blank-states.src'
import {
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/packages/packages-blank-states'
import {packagesRuntimeStore} from '~/composables/store-accessor'
import {appInstance} from '@/utils/app-accessor'
import {runtimeStore} from '@/composables/store-accessor'

import {isAfter, isBefore, parseISO} from 'date-fns'
import {UTCToDate} from '@/utils/helpers'
import {convertPrice} from '@/utils/filters'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {useProductStore} from '~/composables/product-store'

export const usePackagesStore = defineStore('packages', () => {
    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find(item => item.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                hotel: {},
                flight: {},
            }
        )
    }

    const searchRequest = ref(searchRequestBlank())
    const searchResponse: Ref = ref(searchResponseBlank())
    const searchExpirationTime: Ref = ref(null)

    const flightSortKey = ref('priceDeltaAsc')
    const hotelSortKey = ref('priceDeltaAsc')

    const flightFilters: Ref = ref(_flightFilters())
    const hotelFilters: Ref = ref(_hotelFilters())

    const selectedHotelKey: Ref = ref(null)
    const selectedFlightKey: Ref = ref(null)
    const selectedHotel: Ref = ref(null)
    const selectedFlight: Ref = ref(null)

    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse: Ref = ref({})

    const basket: Ref<any[]> = ref([])

    function ADD_TO_BASKET({offerKey, hotel, room, flight, price, searchRequest}: any) {
        basket.value = basket.value.filter(item => isBefore(new Date(), UTCToDate(parseISO(item.hotel.expirationTime))))
        const basketIndex = basket.value.findIndex(item => item.offerKey === offerKey),
            totalPrice = packagesRuntimeStore.totalPrice(room, flight, price),
            newBasketItem = {offerKey, hotel, room, flight, price, searchRequest, totalPrice}
        if (basketIndex !== -1) {
            basket.value[basketIndex] = newBasketItem
        } else {
            basket.value.push(newBasketItem)
        }
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        basketItem.currentPrice = price
    }

    function NEW_SEARCH(_searchRequest: any) {
        flightFilters.value = _flightFilters()
        hotelFilters.value = _hotelFilters()
        searchRequest.value = _searchRequest
        selectedHotelKey.value = null
        selectedFlightKey.value = null
    }

    function RESET() {
        searchRequest.value = searchRequestBlank()
        prepareBookRequest.value = {}
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function RESET_FLIGHT_FILTERS() {
        flightFilters.value = _flightFilters()
    }

    function RESET_HOTEL_FILTERS() {
        hotelFilters.value = _hotelFilters()
    }

    function SET_FLIGHT_FILTER(data: any) {
        flightFilters.value[data.key] = data.value
    }

    function SET_HOTEL_FILTER(data: any) {
        hotelFilters.value[data.key] = data.value
    }

    function SET_FLIGHT_SORT_KEY(sortKey: any) {
        flightSortKey.value = sortKey
    }

    function SET_HOTEL_SORT_KEY(sortKey: any) {
        hotelSortKey.value = sortKey
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP(data: any) {
        prepareBookRequest.value[data.prop] = data.val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function SET_SELECTED_HOTEL({offerKey, offer}: any) {
        selectedHotelKey.value = offerKey
        selectedHotel.value = offer
    }

    function SET_SELECTED_FLIGHT({offerKey, offer}: any) {
        selectedFlightKey.value = offerKey
        selectedFlight.value = offer
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            const basketItem = _getBasketItem(rq.offerKey)
            const {offerKey} = await appInstance.$api.updatePackagePrice.put({
                offerKey: basketItem.offerKey,
                offersIds: [
                    {
                        type: 'FLIGHT',
                        offerId: basketItem.flight.offerKey,
                    },
                    {
                        type: 'ACCOMMODATION',
                        offerId: basketItem.room.groupedOffers[0].offerKey,
                    },
                ],
            })
            NEW_SEARCH(searchRequestBlank())
            rq.offerKey = offerKey
            SET_PREPARE_BOOK_REQUEST(rq)
            const prepareBookResponse = await appInstance.$api.prepareDynamicPackageBook.post(rq)
            SET_PREPARE_BOOK_RESPONSE(prepareBookResponse)
            const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
            if (convertPrice(price).amount !== convertPrice(basketItem.totalPrice).amount) {
                REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
            }
            return prepareBookResponse
        } catch (e) {
            console.error(e)
            throw e
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const offerKey = computed(() => {
        return searchResponse.value.offerKey
    })

    const selectedRoom = computed(() => {
        return selectedHotel.value
            ? selectedHotel.value.rooms.find(
                  ({groupedOffers}: any) => groupedOffers[0].offerKey === selectedHotelKey.value
              )
            : null
    })

    const initialPrice = computed(() => {
        return Object.entries(searchResponse.value.clientPrice).length !== 0
            ? searchResponse.value.clientPrice.price
            : {amount: 0}
    })

    const needUpdateRooms = computed(() => {
        return (hotel: any) =>
            appInstance.$conf.public.refreshHotelOffersSuppliers.includes(hotel.supplierCode) && !hotel.updatedRooms
    })

    const isOffersExpired = computed(() => {
        return () =>
            hasOffers.value && !packagesRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    })

    const basketItem = computed(() => {
        return (offerKey: any) => _getBasketItem(offerKey)
    })

    const hasOffers = computed(() => {
        return searchResponse.value.offers.hotelsCount && searchResponse.value.offers.flightsCount
    })

    return {
        ...useProductStore(searchResponse),
        searchRequest,
        searchResponse,
        searchExpirationTime,
        flightSortKey,
        hotelSortKey,
        flightFilters,
        hotelFilters,
        selectedHotelKey,
        selectedFlightKey,
        selectedHotel,
        selectedFlight,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        NEW_SEARCH,
        RESET,
        SET_SEARCH_REQUEST,
        RESET_FLIGHT_FILTERS,
        RESET_HOTEL_FILTERS,
        SET_FLIGHT_FILTER,
        SET_HOTEL_FILTER,
        SET_FLIGHT_SORT_KEY,
        SET_HOTEL_SORT_KEY,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        SET_SELECTED_HOTEL,
        SET_SELECTED_FLIGHT,
        prepareBook,
        offerKey,
        selectedRoom,
        initialPrice,
        needUpdateRooms,
        isOffersExpired,
        basketItem,
        hasOffers,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(usePackagesStore, import.meta.hot))
